import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Container,
} from '@mui/material';
import {
  Launch,
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE from '../apiBase';
import GlobalFilters from '../components/GlobalFilters';
import StatsCards from '../components/StatsCards';
import TrendChart from '../components/TrendChart';
import OpenPrsCard from '../components/OpenPrsCard';
import DetailsTable from '../components/DetailsTable';
import { NoProjectsEmptyState } from '../components/dashboard/EmptyState';
import { PRAnalysisSkeleton } from '../components/analysis/AnalysisPageSkeleton';
import { 
  Clock,
  GitPullRequest,
  TrendingDown,
  Filter,
} from "lucide-react";

const PRAnalysis = () => {
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [prData, setPrData] = useState([]);
  const [prStats, setPrStats] = useState({
    total_prs: 0,
    avg_cycle_time: 0,
    cycle_time_trend: 0,
    open_prs: 0,
    open_prs_by_repo: [],
  });
  const [trendChartData, setTrendChartData] = useState([]);

  const [filters, setFilters] = useState({
    dateRange: '30d',
    customStart: '',
    customEnd: ''
  });

  // Fetch projects on mount
  const fetchProjects = useCallback(async () => {
    try {
      const projectsUrl = `${API_BASE}/api/projects`;
      const res = await axios.get(projectsUrl);

      setProjects(res.data);
      if (res.data.length > 0) {
        setSelectedProject(res.data[0].api_key);
      } else {
        // No projects: show empty state instead of error
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch projects. Please try again.');
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch PR data and summary when project or filters change
  // Single comprehensive API call to fetch all PR analytics data
  const fetchPRAnalytics = useCallback(async () => {
    if (!selectedProject) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const trendAnalysisUrl = `${API_BASE}/api/analytics/pr-trend-analysis`;
      const params = {
        project_key: selectedProject,
        date_range: filters.dateRange
      };

      if (filters.dateRange === 'custom' && filters.customStart && filters.customEnd) {
        params.start_date = filters.customStart;
        params.end_date = filters.customEnd;
      }

      const res = await axios.get(trendAnalysisUrl, { params });
      const data = res.data;

      // Extract data for different components from the comprehensive response
      setPrStats({
        total_prs: data.current_period.total_prs || 0,
        avg_cycle_time: data.current_period.avg_cycle_time || 0,
        cycle_time_trend: data.trend_analysis.cycle_time_trend || 0,
        open_prs: data.current_period.open_prs || 0,
        open_prs_by_repo: data.current_period.open_prs_by_repo || [],
      });

      setTrendChartData(data.current_period.trend_chart_data || []);
      setPrData(data.current_period.pr_details || []);

      console.log('🔍 Debug - Comprehensive API Response:', data);
      console.log('🔍 Debug - Trend Analysis:', data.trend_analysis);
      console.log('🔍 Debug - Current Period:', data.current_period);
      console.log('🔍 Debug - Previous Period:', data.previous_period);

    } catch (err) {
      console.error('Failed to fetch PR analytics:', err);
      setError('Failed to fetch PR analytics data. Please try again.');
      setPrStats({
        total_prs: 0,
        avg_cycle_time: 0,
        cycle_time_trend: 0,
        open_prs: 0,
        open_prs_by_repo: [],
      });
      setTrendChartData([]);
      setPrData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProject, filters]);


  useEffect(() => {
    fetchPRAnalytics();
  }, [fetchPRAnalytics]);

  const cycleTimeTrendData = useMemo(() => {
    // Transform API data to match chart component expectations
    const raw = (trendChartData || []).map(item => {
      const d = item.date ? new Date(item.date) : null;
      const isValid = d && !isNaN(d.getTime());
      return {
        label: isValid ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (item.date || ''),
        average_value: item.avg_cycle_time || 0,
        total_value: item.avg_cycle_time || 0,
        date: isValid ? d : undefined
      };
    });

    // Pad series to cover full selected date range on X-axis
    const padRange = () => {
      let start = null;
      let end = null;
      const today = new Date();
      const toStartOfDay = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      const toEndOfDay = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59, 59, 999);

      switch (filters.dateRange) {
        case '7d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setDate(s.getDate() - 7);
          start = toStartOfDay(s);
          break;
        }
        case '30d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setDate(s.getDate() - 30);
          start = toStartOfDay(s);
          break;
        }
        case '90d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setDate(s.getDate() - 90);
          start = toStartOfDay(s);
          break;
        }
        case '180d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setDate(s.getDate() - 180);
          start = toStartOfDay(s);
          break;
        }
        case 'custom': {
          if (filters.customStart && filters.customEnd) {
            const s = new Date(filters.customStart);
            const e = new Date(filters.customEnd);
            if (!isNaN(s) && !isNaN(e)) {
              start = toStartOfDay(s);
              end = toEndOfDay(e);
            }
          }
          break;
        }
        default: {
          // For other/overall, do not pad
          return raw;
        }
      }

      if (!start || !end) return raw;

      const byDay = new Map();
      raw.forEach((pt) => {
        if (pt.date) {
          const key = pt.date.toISOString().slice(0, 10);
          byDay.set(key, pt);
        }
      });

      const padded = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        const existing = byDay.get(key);
        if (existing) {
          padded.push(existing);
        } else {
          padded.push({
            label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            average_value: 0,
            total_value: 0,
            date: new Date(d)
          });
        }
      }
      return padded;
    };

    return padRange();
  }, [trendChartData, filters.dateRange, filters.customStart, filters.customEnd]);

  // Memoized stats data for StatsCards component
  const statsData = useMemo(() => [
    {
      id: 'total_prs',
      title: 'Total PRs',
      value: prStats.total_prs,
      unit: '',
      subtitle: 'PRs created',
      icon: <GitPullRequest />,
      isTrend: false
    },
    {
      id: 'avg_cycle_time',
      title: 'Avg Cycle Time',
      value: prStats.avg_cycle_time.toFixed(1),
      unit: 'h',
      subtitle: 'Current average',
      icon: <Clock />,
      isTrend: false
    },
    {
      id: 'cycle_time_trend',
      title: 'Cycle Time Trend',
      value: prStats.cycle_time_trend.toFixed(1),
      unit: '%',
      subtitle: prStats.cycle_time_trend === 0 ? 'No previous data' : 'vs previous period',
      icon: <TrendingDown />,
      isTrend: true,
      invertColors: true
    },
    {
      id: 'open_prs',
      title: 'Open PRs',
      value: prStats.open_prs,
      unit: '',
      subtitle: 'Awaiting review',
      icon: <GitPullRequest />,
      isTrend: false
    }
  ], [prStats]);

  // Memoized open PRs data for sidebar
  const openPRsData = useMemo(() => {
    return prStats.open_prs_by_repo?.map(repo => {
      const repoName = repo.repository || 'Unknown Repository';
      return {
        id: repoName,
        name: repoName,
        count: repo.count || 0,
        subtitle: 'Avg: 1d 5h' // You can calculate this from actual data
      };
    }).filter(item => item.count > 0) || []; // Allow "Unknown Repository" to be displayed
  }, [prStats.open_prs_by_repo]);

  // Table columns configuration
  const tableColumns = useMemo(() => [
    {
      field: 'repository',
      headerName: 'Repository',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'pr_number',
      headerName: 'PR #',
      width: 80,
      minWidth: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#1f1f1f',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            #{params.value}
          </Typography>
          <IconButton
            size="small"
            sx={{
              color: '#1f1f1f',
              p: { xs: 0.25, sm: 0.5 },
              '& svg': { fontSize: { xs: '0.475rem', sm: '0.675rem' } }
            }}
          >
            <Launch fontSize="small" />
          </IconButton>
        </Box>
      )
    },
    {
      field: 'title',
      headerName: 'Title',
      minWidth: 200,
      flex: 2,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'author',
      headerName: 'Author',
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Avatar sx={{
            width: { xs: 16, sm: 20 },
            height: { xs: 16, sm: 20 },
            bgcolor: '#1f1f1f',
            fontSize: { xs: '0.3rem', sm: '0.425rem' },
            fontWeight: 600
          }}>
            {params.value?.substring(0, 2).toUpperCase() || 'A'}
          </Avatar>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      minWidth: 100,
      renderCell: (params) => {
        const status = params.value;
        const getStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case 'open':
              return { bg: '#3b82f6', color: '#ffffff' }; // blue
            case 'merged':
              return { bg: '#10b981', color: '#ffffff' }; // green
            case 'closed':
              return { bg: '#6b7280', color: '#ffffff' }; // gray
            default:
              return { bg: '#f3f4f6', color: '#374151' }; // light gray
          }
        };

        const colors = getStatusColor(status);

        return (
          <Chip
            label={status || 'Unknown'}
            size="small"
            sx={{
              bgcolor: colors.bg,
              color: colors.color,
              fontWeight: 500,
              fontSize: { xs: '0.625rem', sm: '0.75rem' },
              height: { xs: 20, sm: 24 },
              textTransform: 'capitalize'
            }}
          />
        );
      }
    },
    {
      field: 'cycle_time_hours',
      headerName: 'Cycle Time',
      width: 100,
      minWidth: 100,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || typeof value !== 'number') {
          return (
            <Typography
              variant="body2"
              color="#737373"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              N/A
            </Typography>
          );
        }
        const hours = value.toFixed(2);
        const days = Math.floor(value / 24);
        const remainingHours = value % 24;
        const displayValue = days > 0 ? `${days}d ${remainingHours.toFixed(0)}h` : `${hours}h`;

        const getColor = (hours) => {
          if (hours <= 24) return '#10b981'; // green
          if (hours <= 72) return '#f59e0b'; // yellow
          return '#ef4444'; // red
        };

        return (
          <Chip
            label={displayValue}
            size="small"
            sx={{
              bgcolor: `#fff`,
              color: getColor(value),
              fontWeight: 500,
              fontSize: { xs: '0.625rem', sm: '0.75rem' },
              height: { xs: 20, sm: 24 }
            }}
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 90,
      minWidth: 90,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return (
          <Typography
            variant="body2"
            color="#737373"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            N/A
          </Typography>
        );
        const date = new Date(value);
        if (isNaN(date.getTime())) return (
          <Typography
            variant="body2"
            color="#737373"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Invalid
          </Typography>
        );

        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        let displayValue;
        if (diffDays === 1) displayValue = 'Yesterday';
        else if (diffDays < 7) displayValue = `${diffDays}d ago`;
        else displayValue = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return (
          <Typography
            variant="body2"
            color="#737373"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {displayValue}
          </Typography>
        );
      }
    }
  ], []);



  const handleFilterChange = useCallback((name, value) => {
    if (name === 'projectKey') {
      setSelectedProject(value);
      setFilters((prev) => ({ ...prev, projectKey: value }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  }, []);



  return (
    <Box sx={{
      bgcolor: '#fbfbfb',
      minHeight: '100vh',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        {/* Filters Bar at Top */}
        <Box sx={{
          borderRadius: 1,
          p: { xs: 2, sm: 4 },
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          mb: { xs: 3, md: 4 }
        }}>
          {/* Filters Section */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            color: '#737373',
          }}>
            <Filter size={14}/>
            <Typography
              variant="body2"
              sx={{
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
              }}
            >
              Filters:
            </Typography>
            <GlobalFilters
              filters={{ ...filters, projectKey: selectedProject }}
              onFilterChange={handleFilterChange}
              projects={projects}
              showProjectFilter
              showDateRangeFilter
              // showCustomRangeFilter
            />
          </Box>
        </Box>

        {/* No Projects State (top of content) */}
        {!isLoading && Array.isArray(projects) && projects.length === 0 && (
          <NoProjectsEmptyState
            onAction={() => (window.location.href = '/projects')}
            actionText="Create Project"
          />
        )}

        {isLoading && <PRAnalysisSkeleton />}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2
            }}
          >
            {error}
          </Alert>
        )}

        {!isLoading && !error && Array.isArray(projects) && projects.length > 0 && (
          <>
            {/* Stats Cards */}
            <StatsCards stats={statsData} loading={isLoading} />

            {/* Chart and Open PRs Section */}
            <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mb: { xs: 4, md: 5 } }}>
              <Grid item xs={12} lg={8}>
                <TrendChart
                  title="Cycle Time Trends"
                  data={cycleTimeTrendData}
                  loading={isLoading}
                  dataKey="average_value"
                  labelKey="Avg Cycle Time"
                  height={{ xs: 300, sm: 350, md: 400 }}
                  valueFormatter={(v) => {
                    if (v === null || v === undefined || typeof v !== 'number') {
                      return '';
                    }
                    return `${v.toFixed(1)}h`;
                  }}
                  color="#14b8a6"
                  emptyMessage="No data available for the selected period."
                  area
                />
              </Grid>

              <Grid item xs={12} lg={4}>
                <OpenPrsCard
                  title="Open PRs Waiting for Review"
                  items={openPRsData}
                  loading={isLoading}
                  searchPlaceholder="Search PRs..."
                  getItemKey={(item) => item.id}
                  getItemName={(item) => item.name}
                  getItemCount={(item) => item.count}
                  getItemSubtitle={(item) => item.subtitle}
                  getAvatarText={(item) => item.name?.substring(0, 2).toUpperCase() || 'PR'}
                  avatarColor="#737373"
                  chipColor="#f7f9fa"
                  chipTextColor="#737373"
                />
              </Grid>
            </Grid>

            {/* PR Details Table */}
            <DetailsTable
              title="PR Details"
              data={prData}
              columns={tableColumns}
              loading={isLoading}
              height={{ xs: 400, sm: 500, md: 600 }}
              searchEnabled={true}
              searchPlaceholder="Search PRs..."
              searchFields={['repository', 'title', 'author', 'status']}
              getRowId={(row) => row.id || `${row.repository}-${row.pr_number}`}
              pageSizeOptions={[5, 10, 25]}
              initialPageSize={{ xs: 5, sm: 10 }}
            />
          </>
        )}
      </Container>
    </Box>
  );
};

export default PRAnalysis; 